import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { Group, Workflow } from '../../types';
import { API_URL } from '../../config';
import { useModal } from '../../context/ModalContext';
import {
  buildPurchaseExportIdParams,
  buildTicketExportIdParams,
  exportFoundCount,
  hasPurchaseExportFilters,
  hasTicketExportFilters,
} from '../exportIdQuery';
import ExportFieldPicker from './ExportFieldPicker';
import ExportFilters, {
  createEmptyExportFilterValues,
  normalizeExportFilterValues,
  ExportFilterValues,
} from './ExportFilters';
import ExportPreview from './ExportPreview';
import {
  ExportArea,
  ExportFieldItem,
  ExportPreviewResponse,
  RecordExportFilters,
  SelectionMode,
  createExportCancelToken,
  downloadExportFile,
  fetchExportFields,
  fetchExportIds,
  fetchExportPreview,
  isExportAbortedError,
  requestExportCancellation,
} from './api';
import '../Dashboard.css';
import './ExportPage.css';

interface Props {
  area: ExportArea;
}

const ExportPage: React.FC<Props> = ({ area }) => {
  const { showAlert } = useModal();
  const [rawFilters, setRawFilters] = useState<ExportFilterValues>(() => createEmptyExportFilterValues());
  const filters = useMemo(() => normalizeExportFilterValues(rawFilters), [rawFilters]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [ids, setIds] = useState<number[]>([]);
  const [foundCount, setFoundCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loadingIds, setLoadingIds] = useState(false);
  const [fields, setFields] = useState<ExportFieldItem[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('automatic');
  const [preview, setPreview] = useState<ExportPreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const cancelTokenRef = useRef<string | null>(null);

  const title = area === 'tickets' ? 'Exportar chamados' : 'Exportar requisições';
  const entityLabel = area === 'tickets' ? 'chamados' : 'requisições';
  const subtitle =
    area === 'tickets'
      ? 'Filtre os chamados e gere a planilha com as colunas resolvidas pelo fluxo. O recorte de conclusão segue o filtro abaixo.'
      : 'Filtre as requisições e gere a planilha com as colunas resolvidas pelo fluxo. O recorte de conclusão segue o filtro abaixo.';

  const hasFilters = useMemo(() => {
    if (area === 'tickets') {
      return hasTicketExportFilters({
        filterWorkflow: filters.filterWorkflow,
        filterGroup: filters.filterGroup,
        filterStatuses: filters.filterStatuses,
        filterPriority: filters.filterPriority,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        searchText: filters.searchText,
        ticketId: filters.recordId,
        includeSubtasks: filters.includeSubtasks,
      });
    }
    return hasPurchaseExportFilters({
      filterWorkflow: filters.filterWorkflow,
      filterGroup: filters.filterGroup,
      filterStatus: filters.filterStatus,
      filterPriority: filters.filterPriority,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      searchText: filters.searchText,
      purchaseRequestId: filters.recordId,
      includeSubtasks: filters.includeSubtasks,
    });
  }, [area, filters]);

  const queryParams = useMemo(() => {
    if (area === 'tickets') {
      return buildTicketExportIdParams({
        filterWorkflow: filters.filterWorkflow,
        filterGroup: filters.filterGroup,
        filterStatuses: filters.filterStatuses,
        filterPriority: filters.filterPriority,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        searchText: filters.searchText,
        ticketId: filters.recordId,
        includeSubtasks: filters.includeSubtasks,
      });
    }
    return buildPurchaseExportIdParams({
      filterWorkflow: filters.filterWorkflow,
      filterGroup: filters.filterGroup,
      filterStatus: filters.filterStatus,
      filterPriority: filters.filterPriority,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      searchText: filters.searchText,
      purchaseRequestId: filters.recordId,
      includeSubtasks: filters.includeSubtasks,
      closedVisibility: filters.closedVisibility,
    });
  }, [area, filters]);

  const requestFilters = useMemo<RecordExportFilters>(() => {
    const body: RecordExportFilters = {
      include_subtasks: filters.includeSubtasks,
      closed_visibility: filters.closedVisibility,
    };
    if (filters.recordId) body.record_id = filters.recordId;
    if (filters.searchText) body.search = filters.searchText;
    if (area === 'tickets' && filters.filterStatuses.length > 0) {
      body.statuses = filters.filterStatuses;
    } else if (filters.filterStatus) {
      body.status = filters.filterStatus;
    }
    if (filters.filterPriority) body.priority = filters.filterPriority;
    if (filters.dateFrom) body.created_from = filters.dateFrom;
    if (filters.dateTo) body.created_to = filters.dateTo;
    if (filters.filterWorkflow) body.workflow_id = Number(filters.filterWorkflow);
    if (filters.filterGroup) body.assigned_group_id = Number(filters.filterGroup);
    return body;
  }, [area, filters]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [workflowResponse, groupResponse] = await Promise.all([
          axios.get(`${API_URL}/api/workflows/published`, { params: { area_type: area } }),
          axios.get(`${API_URL}/api/groups`, { params: { limit: 1000 } }),
        ]);
        setWorkflows(workflowResponse.data || []);
        setGroups((groupResponse.data || []).filter((group: Group) => group.is_active));
      } catch (error) {
        console.error('Erro ao carregar filtros da exportação:', error);
      }
    };
    void loadMeta();
  }, [area]);

  useEffect(() => {
    if (!hasFilters) {
      setIds([]);
      setFoundCount(0);
      setTruncated(false);
      return;
    }
    let cancelled = false;
    setLoadingIds(true);
    fetchExportIds(area, queryParams)
      .then((payload) => {
        if (cancelled) return;
        setIds(payload.ids || []);
        setFoundCount(exportFoundCount(payload, filters.includeSubtasks));
        setTruncated(Boolean(payload.truncated));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Erro ao carregar IDs da exportação:', error);
        setIds([]);
        setFoundCount(0);
        setTruncated(false);
      })
      .finally(() => {
        if (!cancelled) setLoadingIds(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area, filters.includeSubtasks, hasFilters, queryParams]);

  useEffect(() => {
    if (!hasFilters && !filters.filterWorkflow) {
      setFields([]);
      setSelectedIds([]);
      return;
    }
    let cancelled = false;
    setLoadingFields(true);
    fetchExportFields(area, queryParams)
      .then((items) => {
        if (cancelled) return;
        setFields(items);
        setSelectedIds(items.map((item) => item.field_id));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Erro ao carregar campos da exportação:', error);
        setFields([]);
        setSelectedIds([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area, filters.filterWorkflow, hasFilters, queryParams]);

  useEffect(() => {
    if (!hasFilters) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    fetchExportPreview(area, {
      filters: requestFilters,
      custom_field_ids: selectionMode === 'manual' ? selectedIds : undefined,
      selection_mode: selectionMode,
      sample_size: 8,
    })
      .then((payload) => {
        if (cancelled) return;
        setPreview(payload);
        if (payload.columns?.length) {
          setFields((current) => (current.length > 0 ? current : payload.columns));
          setSelectedIds((current) =>
            current.length > 0 ? current : payload.columns.map((item) => item.field_id),
          );
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError('Não foi possível gerar a prévia.');
        console.error('Erro na prévia da exportação:', error);
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area, hasFilters, requestFilters, selectedIds, selectionMode]);

  const patchFilters = useCallback((patch: Partial<ExportFilterValues>) => {
    setDone(false);
    setRawFilters((current) => normalizeExportFilterValues({ ...current, ...patch }));
  }, []);

  const handleExport = async () => {
    if (exporting) return;
    if (!hasFilters || foundCount === 0) {
      showAlert({
        title: 'Nada para exportar',
        message: `Não há ${entityLabel} para exportar. Aplique os filtros e aguarde o carregamento.`,
        variant: 'warning',
      });
      return;
    }
    setExporting(true);
    setDone(false);
    const controller = new AbortController();
    const cancelToken = createExportCancelToken();
    abortRef.current = controller;
    cancelTokenRef.current = cancelToken;
    try {
      await downloadExportFile(
        area,
        {
          filters: requestFilters,
          custom_field_ids: selectionMode === 'manual' ? selectedIds : undefined,
          selection_mode: selectionMode,
          cancel_token: cancelToken,
        },
        controller.signal,
      );
      setDone(true);
    } catch (error) {
      if (isExportAbortedError(error)) return;
      console.error('Erro ao exportar:', error);
      showAlert({
        title: 'Erro ao exportar',
        message: 'Não foi possível gerar o arquivo. Tente novamente.',
        variant: 'danger',
      });
    } finally {
      abortRef.current = null;
      cancelTokenRef.current = null;
      setExporting(false);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    const token = cancelTokenRef.current;
    if (token) void requestExportCancellation(token);
  };

  return (
    <div className="dashboard-page export-page">
      <div className="dashboard-hero mb-3">
        <div className="d-flex justify-content-between align-items-center dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">{title}</h2>
            <small className="text-muted">{subtitle}</small>
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center">
            {loadingIds ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Badge bg="secondary" className="dashboard-badge">
                {foundCount} {entityLabel}
              </Badge>
            )}
            {exporting && (
              <Button variant="outline-danger" size="sm" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
            <Button
              variant="success"
              size="sm"
              onClick={handleExport}
              disabled={exporting || loadingIds || !hasFilters || foundCount === 0}
            >
              {exporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Gerando arquivo...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-excel me-1" aria-hidden />
                  Gerar planilha
                </>
              )}
            </Button>
          </div>
        </div>
        {truncated && (
          <small className="text-muted d-block mt-2">O recorte foi limitado ao máximo configurado no servidor.</small>
        )}
        {done && <small className="text-success d-block mt-2">Arquivo gerado com sucesso.</small>}
        {ids.length > 0 && !done && (
          <small className="text-muted d-block mt-2">A planilha usa o mesmo recorte e as mesmas regras da prévia.</small>
        )}
        {!hasFilters && (
          <small className="text-muted d-block mt-2">Configure pelo menos um filtro para localizar os registros.</small>
        )}
        {hasFilters && !loadingIds && foundCount === 0 && (
          <small className="text-muted d-block mt-2">Nenhum registro encontrado com os filtros aplicados.</small>
        )}
      </div>

      <ExportFilters
        area={area}
        values={filters}
        onChange={patchFilters}
        workflows={workflows}
        groups={groups}
      />

      <Card className="mb-3 dashboard-section-card">
        <Card.Header className="d-flex align-items-center gap-2 py-2">
          <i className="bi bi-ui-checks text-primary" aria-hidden />
          <span className="fw-semibold">Campos da planilha</span>
        </Card.Header>
        <Card.Body>
          <ExportFieldPicker
            fields={fields}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            selectionMode={selectionMode}
            onSelectionModeChange={setSelectionMode}
            loading={loadingFields}
            disabled={exporting}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3 dashboard-section-card">
        <Card.Header className="d-flex align-items-center gap-2 py-2">
          <i className="bi bi-table text-primary" aria-hidden />
          <span className="fw-semibold">Prévia</span>
        </Card.Header>
        <Card.Body>
          <ExportPreview preview={preview} loading={loadingPreview} error={previewError} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ExportPage;
